import { useState } from 'react';
import { EntityCard } from '@/components/EntityCard';
import { generatedStoriesPageMessages } from './generatedStoriesPage.messages';
import { fetchGeneratedStories } from '@/api/services/generated-stories/fetchGeneratedStories';
import type { GeneratedStory } from 'schemas';
import { ApiError } from '@/api/ApiError';
import { UNKNOWN_ERROR_API_ERROR } from '@/api/helpers/unknownError';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Heading3, Muted } from '@/components/Typography';
import { DashboardPageContainer } from '@/components/layout/DashboardPageContainer';
import { CardsGridContainer } from '@/components/layout/CardsGridContainer';
import { transformGeneratedStoryToEntityCard } from '@/pages/GeneratedStoriesPage/helpers/transformGeneratedStoryToEntityCard';
import { sharedMessages } from '@/shared-messages/shared.messages';
import { toast } from 'sonner';
import { useLoadOnce } from '@/hooks/useLoadOnce';
import { GeneratedStoryPreviewModal } from '@/components/GeneratedStoryPreviewModal';
import { printStory } from '@/api/services/generated-stories/printStory';

export function GeneratedStoriesPage() {
  const [generatedStories, setGeneratedStories] = useState<GeneratedStory[]>(
    []
  );
  const [loading, setLoading] = useState(true);

  const [deletingStoryId, setDeletingStoryId] = useState<string | null>(null);
  const [storyToDelete, setStoryToDelete] = useState<GeneratedStory | null>(
    null
  );

  // Preview modal state
  const [previewStory, setPreviewStory] = useState<GeneratedStory | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // PDF dialog state
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);

  const handleDeleteStory = (story: GeneratedStory) => {
    setStoryToDelete(story);
  };

  const confirmDeleteStory = async () => {
    if (!storyToDelete) return;

    try {
      setDeletingStoryId(storyToDelete.id);
      // TODO: Implement soft delete for generated stories
      console.log('deleting story bleep bloop', storyToDelete);
      setGeneratedStories((prevStories) =>
        prevStories.filter((story) => story.id !== storyToDelete.id)
      );

      toast.success(generatedStoriesPageMessages.deleteSuccessMessage);
    } catch {
      toast.error(generatedStoriesPageMessages.deleteErrorMessage);
    } finally {
      setDeletingStoryId(null);
      setStoryToDelete(null);
    }
  };

  const cancelDeleteStory = () => {
    setStoryToDelete(null);
  };

  const handleOpenPreview = (story: GeneratedStory) => {
    setPreviewStory(story);
    setIsPreviewModalOpen(true);
  };

  const handlePrintStory = async (story: GeneratedStory) => {
    try {
      // Show toast notification that PDF is generating
      toast.info(generatedStoriesPageMessages.printGenerating);

      setLoading(true);
      const url = await printStory(story.id);

      // Show dialog with PDF URL
      setPdfUrl(url);
      setIsPdfDialogOpen(true);
    } catch (err) {
      const apiError = err instanceof ApiError ? err : UNKNOWN_ERROR_API_ERROR;
      toast.error(apiError.message || generatedStoriesPageMessages.printError);
    } finally {
      setLoading(false);
    }
  };

  const handleClosePreview = () => {
    setIsPreviewModalOpen(false);
    setPreviewStory(null);
  };

  const handleClosePdfDialog = () => {
    setIsPdfDialogOpen(false);
    setPdfUrl(null);
  };

  const handleDownloadPdf = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  useLoadOnce(() => {
    const loadGeneratedStories = async () => {
      try {
        setLoading(true);

        const fetchedStories = await fetchGeneratedStories();
        setGeneratedStories(fetchedStories);
      } catch (err) {
        const apiError =
          err instanceof ApiError ? err : UNKNOWN_ERROR_API_ERROR;
        toast.error(apiError.message);
      } finally {
        setLoading(false);
      }
    };

    loadGeneratedStories();
  });

  return (
    <DashboardPageContainer
      headline={generatedStoriesPageMessages.title}
      subheading={generatedStoriesPageMessages.subtitle}
    >
      {generatedStories.length === 0 && !loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mb-4 text-4xl">📖</div>
            <Heading3 className="mb-2">
              {generatedStoriesPageMessages.noStoriesFound}
            </Heading3>
            <Muted>{generatedStoriesPageMessages.noStoriesDescription}</Muted>
          </div>
        </div>
      ) : (
        <CardsGridContainer loading={loading}>
          {generatedStories.map((story) => {
            const entityCardProps = transformGeneratedStoryToEntityCard(story);
            const isDeleting = deletingStoryId === story.id;
            return (
              <EntityCard
                key={story.id}
                {...entityCardProps}
                actions={[
                  {
                    actionButtonLabel:
                      generatedStoriesPageMessages.openPreviewAction,
                    onButtonClick: () => handleOpenPreview(story),
                  },
                  {
                    actionButtonLabel: generatedStoriesPageMessages.printAction,
                    onButtonClick: () => handlePrintStory(story),
                  },
                  {
                    actionButtonLabel: isDeleting
                      ? generatedStoriesPageMessages.deleting
                      : generatedStoriesPageMessages.deleteAction,
                    onButtonClick: () => handleDeleteStory(story),
                  },
                ]}
              />
            );
          })}
        </CardsGridContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={storyToDelete !== null}
        onOpenChange={cancelDeleteStory}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {generatedStoriesPageMessages.deleteConfirmTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {generatedStoriesPageMessages.deleteConfirmMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDeleteStory}>
              {sharedMessages.commonButtons.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteStory}
              disabled={deletingStoryId !== null}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deletingStoryId === storyToDelete?.id
                ? generatedStoriesPageMessages.deleting
                : generatedStoriesPageMessages.deleteAction}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Generated Story Preview Modal */}
      <GeneratedStoryPreviewModal
        generatedStory={previewStory}
        isOpen={isPreviewModalOpen}
        onClose={handleClosePreview}
      />

      {/* PDF Download Dialog */}
      <AlertDialog open={isPdfDialogOpen} onOpenChange={handleClosePdfDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {generatedStoriesPageMessages.pdfDialogTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {generatedStoriesPageMessages.pdfDialogDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleClosePdfDialog}>
              {sharedMessages.commonButtons.cancel}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDownloadPdf}>
              {generatedStoriesPageMessages.downloadPdfButton}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardPageContainer>
  );
}
